import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';

/**
 * The pager of the tables: the previous and next buttons around up to
 * `pageLinkSize` page numbers, the current page centred as far as possible.
 * `first` is the index of the first row of the page.
 */
@Component({
  selector: 'app-paginator',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<div class="app-paginator {{ styleClass }}" role="navigation">
    @if (showFirstLastIcon) {
      <button
        type="button"
        class="app-paginator-first app-paginator-element"
        aria-label="First Page"
        [disabled]="isFirst"
        [class.app-disabled]="isFirst"
        (click)="changePage(0)"
      >
        <span class="app-paginator-icon app-icon fa-solid fa-angles-left"></span>
      </button>
    }
    <button
      type="button"
      class="app-paginator-prev app-paginator-element"
      aria-label="Previous Page"
      [disabled]="isFirst"
      [class.app-disabled]="isFirst"
      (click)="changePage(page - 1)"
    >
      <span class="app-paginator-icon app-icon fa-solid fa-angle-left"></span>
    </button>
    <span class="app-paginator-pages">
      @for (link of pageLinks; track link) {
        <button
          type="button"
          class="app-paginator-page app-paginator-element"
          [class.app-highlight]="link - 1 === page"
          [attr.aria-current]="link - 1 === page ? 'page' : null"
          (click)="changePage(link - 1)"
        >
          {{ link }}
        </button>
      }
    </span>
    <button
      type="button"
      class="app-paginator-next app-paginator-element"
      aria-label="Next Page"
      [disabled]="isLast"
      [class.app-disabled]="isLast"
      (click)="changePage(page + 1)"
    >
      <span class="app-paginator-icon app-icon fa-solid fa-angle-right"></span>
    </button>
    @if (showFirstLastIcon) {
      <button
        type="button"
        class="app-paginator-last app-paginator-element"
        aria-label="Last Page"
        [disabled]="isLast"
        [class.app-disabled]="isLast"
        (click)="changePage(pageCount - 1)"
      >
        <span class="app-paginator-icon app-icon fa-solid fa-angles-right"></span>
      </button>
    }
  </div>`,
})
export class PaginatorComponent implements OnChanges {
  @Input() totalRecords = 0;
  @Input() rows = 10;
  @Input() first = 0;
  @Input() pageLinkSize = 5;
  @Input() showFirstLastIcon = true;
  @Input() styleClass = '';

  /** The first row of the page a user went to. */
  @Output() firstChange = new EventEmitter<number>();

  pageLinks: number[] = [];

  get page() {
    return Math.floor(this.first / this.rows);
  }

  get pageCount() {
    return Math.ceil(this.totalRecords / this.rows);
  }

  get isFirst() {
    return this.page === 0;
  }

  get isLast() {
    return this.page >= this.pageCount - 1;
  }

  ngOnChanges(): void {
    this.updatePageLinks();
  }

  changePage(page: number) {
    if (page >= 0 && page < this.pageCount && page !== this.page) {
      this.first = page * this.rows;
      this.updatePageLinks();
      this.firstChange.emit(this.first);
    }
  }

  private updatePageLinks() {
    const count = this.pageCount;
    const visible = Math.min(this.pageLinkSize, count);
    let start = Math.max(0, Math.ceil(this.page - visible / 2));
    const end = Math.min(count - 1, start + visible - 1);
    start = Math.max(0, start - (this.pageLinkSize - (end - start + 1)));
    this.pageLinks = [];
    for (let link = start + 1; link <= end + 1; link++) {
      this.pageLinks.push(link);
    }
  }
}
